import React, { useContext, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import SettingNavigation from "../../../components/SettingNavigation/SettingNavigation";

import { ProductSelectSettingsContainer } from "../../../containers/ProductsSelectSettingContainer/ProductsSelectSettingContainer";
import { QuoteGroupContext } from "../../../providers/RootContextProvider";
import QuoteSettingsEditor, {
    ProductSelectionState
} from "../../../components/Quotes/Components/QuoteSettingsEditor/QuoteSettingsEditor";
import {
    ClientProductTypes,
    isClientProductType
} from "../../../../common/interfaces/enums/ProductType";
import useAPI from "../../../hooks/useAPI";
import EditClientButton from "../../../components/EditClientsButton/EditClientButton";
import DapWarningDialog from "../../../../digital-advice/components/Modals/DapWarningDialog";
import useIsMounted from "../../../../common/hooks/useIsMounted";

const ProductSelectPageContentMultiProductEdit: React.FC = () => {
    const { selectors, actions } = useContext(QuoteGroupContext);
    const { setProductSelected } = useAPI();

    const getProductState = (): ProductSelectionState => {
        const state: ProductSelectionState = {};

        selectors.getAllSettings().forEach(
            (s) =>
                (state[s.productType] = {
                    hasSettings: s.caseId > 0,
                    isSelected:
                        isClientProductType(s.productType) &&
                        selectors.isProductEnabled(s.productType),
                    hasChanged: false
                })
        );

        return state;
    };
    const history = useHistory();
    const isMounted = useIsMounted();
    const search = new URLSearchParams(location.search);

    const [productState, setProductState] = useState<ProductSelectionState>(
        getProductState()
    );
    const [isApplyingChanges, setIsApplyingChanges] = useState(false);
    const [isFromDAP, setIsFromDAP] = useState(false);
    const [isOpenWarningDialogModal, setIsOpenWarningDialogModal] = useState(false);
    const [warningNextStepUrl, setWarningNextStepUrl] = useState("");

    const hasSelectedProducts = Object.values(productState).some((s) => s?.isSelected);

    const handleWarningDialogModal = () => {
        const isQuoteWarningConfirm =
            !!window.localStorage.getItem("quote-modified-warning") || false;
        if (!isQuoteWarningConfirm) {
            setIsOpenWarningDialogModal(true);
        } else {
            history.push(warningNextStepUrl);
        }
    };

    useEffect(() => {
        setIsFromDAP(search.has("da"));
    }, [isFromDAP]);

    return (
        <React.Fragment>
            <DapWarningDialog
                isOpen={isOpenWarningDialogModal}
                onClose={(isContinue: boolean) => {
                    if (isContinue) history.push(warningNextStepUrl);
                    isMounted.current && setIsOpenWarningDialogModal(false);
                }}
            />
            <ProductSelectSettingsContainer
                heading="Select the types of insurance you'd like to compare"
                className="layout-flex-column--centered">
                <QuoteSettingsEditor
                    className="width-full"
                    productState={productState}
                    setProductState={setProductState}
                    hasSelectedProducts={hasSelectedProducts}
                    isFromDAP={isFromDAP}
                    showWarning={handleWarningDialogModal}
                    setWarningNextStepUrl={setWarningNextStepUrl}
                />
                <EditClientButton
                    isFromDAP={isFromDAP}
                    showWarning={handleWarningDialogModal}
                    setWarningNextStepUrl={setWarningNextStepUrl}
                />
            </ProductSelectSettingsContainer>
            <SettingNavigation
                isDisabledCallToAction={!hasSelectedProducts || isApplyingChanges}
                onClickCallToAction={async () => {
                    setIsApplyingChanges(true);
                    const productsToUpdate: Promise<void>[] = Object.keys(productState)
                        .filter((s) => productState[s]?.hasChanged)
                        .map(async (s: ClientProductTypes) =>
                            setProductSelected(s, productState[s]?.isSelected)
                        );

                    await Promise.all(productsToUpdate)
                        .then(() => {
                            actions.clearQuoteResults();
                        })
                        .finally(() => {
                            setIsApplyingChanges(false);
                            history.push("/results");
                        });
                }}
                onClickBack={() => {
                    history.push(`/results`);
                }}
                isEditing
            />
        </React.Fragment>
    );
};

export default ProductSelectPageContentMultiProductEdit;
